import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { Clock, CheckCircle, AlertCircle, ChevronRight, FileText, Plus, Filter, Search, Package, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';

const PackingList = ({ db, user }) => {
  const navigate = useNavigate();
  const [mocs, setMocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('All');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    // Fetch MOCs that are in packing, ready to be scheduled, on hold, or rejected
    const q = query(
      collection(db, "dispatches"), 
      where("dispatchStatus", "in", ["packing_queued", "packing_scheduled", "packing", "packed_ready", "packing_hold", "packing_rejected"])
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      console.log("Packing Queue Snapshot received. Count:", querySnapshot.size);
      const mocList = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
      setMocs(mocList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching Packing list: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const renderStatus = (status) => {
    switch (status) {
      case 'packing_queued':
        return <span style={{ color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><Clock size={14} /> Awaiting Schedule</span>;
      case 'packing_scheduled':
        return <span style={{ color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><CheckCircle size={14} /> Scheduled</span>;
      case 'packing':
        return <span style={{ color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><Package size={14} /> Packing</span>;
      case 'packing_hold':
        return <span style={{ color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><Clock size={14} /> Hold</span>;
      case 'packing_rejected':
        return <span style={{ color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><AlertCircle size={14} /> Rejected</span>;
      case 'packed_ready':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><CheckCircle size={14} /> Packing Done</span>;
      case 'invoice_pending':
      case 'invoice_ready':
      case 'dc_confirmed':
        return <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', fontWeight: '600' }}><CheckCircle size={14} /> QC Pending</span>;
      default:
        return <span style={{ color: '#64748b' }}>{status?.replace(/_/g, ' ')}</span>;
    }
  };

  const groupedMocs = useMemo(() => {
    const list = mocs.filter(moc => {
      const matchesSearch = 
        moc.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moc.mocNo?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesPriority = filterPriority === 'All' || moc.priority === filterPriority;

      return matchesSearch && matchesPriority;
    });

    // Sort by Date, then Sequence
    list.sort((a, b) => {
        const dateA = a.scheduledDate || '9999-12-31';
        const dateB = b.scheduledDate || '9999-12-31';
        if (dateA < dateB) return -1;
        if (dateA > dateB) return 1;

        const seqA = (a.sequence === undefined || a.sequence === null || a.sequence === 0) ? 999999 : a.sequence;
        const seqB = (b.sequence === undefined || b.sequence === null || b.sequence === 0) ? 999999 : b.sequence;
        return seqA - seqB;
    });

    // Group by Date
    const groups = {};
    list.forEach(moc => {
        const dateKey = moc.scheduledDate || 'Not Set';
        if (!groups[dateKey]) groups[dateKey] = [];
        groups[dateKey].push(moc);
    });

    // Return sorted array of groups to guarantee chronological order when rendering
    return Object.keys(groups).sort((a, b) => {
        if (a === 'Not Set') return 1;
        if (b === 'Not Set') return -1;
        return a.localeCompare(b);
    }).map(dateKey => ({
        dateKey,
        items: groups[dateKey]
    }));
  }, [mocs, searchTerm, filterPriority]);

  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'High': return { color: '#ef4444', backgroundColor: '#fee2e2' };
      case 'Medium': return { color: '#f59e0b', backgroundColor: '#fef3c7' };
      case 'Low': return { color: '#10b981', backgroundColor: '#d1fae5' };
      default: return { color: '#6b7280', backgroundColor: '#f3f4f6' };
    }
  };

  const formatDate = (dateString) => {
      if (!dateString) return 'Not Set';
      // Handle "YYYY-MM-DD" from the date input
      if (typeof dateString === 'string' && dateString.length === 10) {
        return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
      }
      
      const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
      return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const formatDateHeader = (dateString) => {
      if (!dateString || dateString === 'Not Set') return 'Unscheduled / Not Set';
      const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-IN', options);
  };
  
  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Packing Queue</h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Schedule and manage material packing sequences.</p>
        </div>
      </div>

      {/* FILTERS */}
      <div style={filterBarStyle}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search style={searchIconStyle} size={18} />
          <input 
            type="text" 
            placeholder="Search by Customer or MOC No..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={searchInputStyle}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Filter size={18} color="#64748b" />
          <select 
            value={filterPriority} 
            onChange={(e) => setFilterPriority(e.target.value)}
            style={selectInputStyle}
          >
            <option value="All">All Priorities</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
        </div>
      </div>

      {groupedMocs.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <Package size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#374151' }}>Queue is Clear!</h3>
          <p style={{ color: '#64748b' }}>No MOCs are currently awaiting packing.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          {groupedMocs.map((group) => (
            <div key={group.dateKey}>
              <h3 style={{ fontSize: '15px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                <Calendar size={18} color="#2563eb" /> 
                {formatDateHeader(group.dateKey)}
                <span style={{ fontSize: '12px', backgroundColor: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '12px', marginLeft: '5px' }}>
                  {group.items.length}
                </span>
              </h3>
              <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ backgroundColor: '#f9fafb' }}>
                    <tr>
                      <th style={thStyle}>Seq</th>
                      <th style={thStyle}>Priority</th>
                      <th style={{...thStyle, textAlign:'left'}}>Customer / MOC</th>
                      <th style={thStyle}>Scheduled Date</th>
                      <th style={thStyle}>Packed On</th>
                      <th style={{...thStyle, textAlign:'left'}}>Remarks</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.items.map((moc) => (
                      <tr 
                        key={moc.id} 
                        style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                        onClick={() => navigate(`/ops/packing/${moc.id}`)}
                        className="hover-highlight"
                      >
                        <td style={{...tdStyle, fontWeight: 'bold', color: '#2563eb'}}>{(moc.sequence === 0 || !moc.sequence) ? '--' : `#${moc.sequence}`}</td>
                        <td style={tdStyle}>
                          <span style={{ 
                              padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 'bold',
                              ...getPriorityStyle(moc.priority)
                          }}>
                              {moc.priority || 'NORMAL'}
                          </span>
                        </td>
                        <td style={{...tdStyle, textAlign:'left'}}>
                          <div style={{fontWeight: 'bold', fontSize: '14px'}}>{moc.customerName}</div>
                          <div style={{fontSize: '11px', color: '#6b7280'}}>MOC: {moc.mocNo}</div>
                        </td>
                        <td style={tdStyle}>{formatDate(moc.scheduledDate)}</td>
                        <td style={tdStyle}>
                          {moc.packedAt ? formatDate(moc.packedAt?.toDate ? moc.packedAt.toDate() : moc.packedAt) : '--'}
                        </td>
                        <td style={{...tdStyle, textAlign:'left', fontSize: '12px', color: '#4b5563', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                          {moc.dispatchStatus === 'packed_ready' ? (moc.packingRemarks || '--') : (moc.warehouseRemarks || '--')}
                        </td>
                        <td style={tdStyle}>
                          {renderStatus(moc.dispatchStatus)}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', color: '#2563eb', fontWeight: '600' }}>
                            {['packed_ready', 'packing_rejected'].includes(moc.dispatchStatus) ? 'View Record' : 'Schedule'} <ChevronRight size={16} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };

const filterBarStyle = { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' };
const searchInputStyle = { width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none' };
const searchIconStyle = { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' };
const selectInputStyle = { padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '14px', backgroundColor: 'white', outline: 'none', minWidth: '160px' };

export default PackingList;
